import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Button, Input, Checkbox } from '../atoms';
import { useToast } from './Toast';

/**
 * SavedViewMenu — the per-list-page "Saved views" control (PRD 06.2).
 *
 * Renders a "Saved views" button that opens a dropdown listing the caller's
 * visible saved views for ONE list surface (own + role + org, resolved by the
 * backend) and lets them:
 *   - APPLY a view  → calls onApply(config) so the host list restores the saved
 *     {q, filters, sort, dir, columns} envelope,
 *   - SAVE the current filter/sort state as a new view (personal, or shared/org
 *     when the caller is admin-tier and ticks "Share with team"),
 *   - DELETE a view they own (or any, if admin-tier — the server enforces this).
 *
 * Backed by the REAL shared preference store (/api/saved-views, migration 031).
 * The `entityType` prop is the registry_key for a view_type='list' surface
 * (e.g. 'parcels', 'clients') — the store validates it against the registry.
 *
 * Lane build 06.2-06.3.
 */

export interface SavedView {
  id: string;
  organization_id: string;
  owner_user_id: string | null;
  scope: 'user' | 'role' | 'org';
  role: string | null;
  view_type: string;
  registry_key: string;
  name: string;
  config: Record<string, unknown>;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface SavedViewMenuProps {
  /** registry_key of the view_type='list' surface, e.g. 'parcels'. */
  entityType: string;
  /** The current filter/sort/column state to persist when saving a new view. */
  currentConfig: Record<string, unknown>;
  /** Called with a saved view's config when the caller applies it. */
  onApply: (config: Record<string, unknown>) => void;
  /** Whether the caller may create shared (org-scoped) views. Default false. */
  canShare?: boolean;
  /** Current user id — enables the delete affordance on the caller's own views. */
  currentUserId?: string;
}

const scopeLabel: Record<SavedView['scope'], string> = {
  user: 'Personal',
  role: 'Role',
  org: 'Shared',
};

export function SavedViewMenu({ entityType, currentConfig, onApply, canShare = false, currentUserId }: SavedViewMenuProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [shareWithTeam, setShareWithTeam] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const listKey = ['saved-views', 'list', entityType];

  const viewsQuery = useQuery<SavedView[]>({
    queryKey: listKey,
    queryFn: ({ signal }) => api.get<SavedView[]>(`/api/saved-views?entity_type=${encodeURIComponent(entityType)}`, signal),
    enabled: open,
    staleTime: 30_000,
  });

  const createMutation = useMutation({
    mutationFn: (body: { name: string; config: Record<string, unknown>; shared: boolean }) =>
      api.post<SavedView>('/api/saved-views', {
        entity_type: entityType,
        name: body.name,
        config: body.config,
        shared: body.shared,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listKey });
      setCreating(false);
      setNewName('');
      setShareWithTeam(false);
      toast({ variant: 'success', message: 'Saved view created.' });
    },
    onError: (err) => {
      const msg = err instanceof ApiClientError && err.status === 403
        ? 'You do not have permission to create a shared view.'
        : 'Could not save this view.';
      toast({ variant: 'error', message: msg });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/api/saved-views/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: listKey });
      toast({ variant: 'success', message: 'Saved view deleted.' });
    },
    onError: () => toast({ variant: 'error', message: 'Could not delete this view.' }),
  });

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const views = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data]);

  function apply(view: SavedView) {
    onApply(view.config || {});
    setOpen(false);
  }

  function submitCreate() {
    const name = newName.trim();
    if (!name) return;
    createMutation.mutate({ name, config: currentConfig, shared: shareWithTeam && canShare });
  }

  const canDelete = (v: SavedView) => canShare || (v.owner_user_id != null && v.owner_user_id === currentUserId);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <Button
        variant="secondary"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        iconLeft={
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
        }
      >
        Saved views
      </Button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 50,
            width: 300,
            maxHeight: 440,
            overflowY: 'auto',
            background: 'var(--color-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--r-lg)',
            boxShadow: '0 8px 24px rgba(15,23,24,0.16)',
            padding: 'var(--sp-2)',
          }}
        >
          {viewsQuery.isLoading && (
            <div style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-2)', fontSize: 'var(--fs-sm)' }}>
              Loading views…
            </div>
          )}
          {viewsQuery.isError && (
            <div style={{ padding: 'var(--sp-3)', color: 'var(--color-status-red)', fontSize: 'var(--fs-sm)' }}>
              Could not load saved views.
            </div>
          )}
          {!viewsQuery.isLoading && !viewsQuery.isError && views.length === 0 && (
            <div style={{ padding: 'var(--sp-3)', color: 'var(--color-ink-2)', fontSize: 'var(--fs-sm)' }}>
              No saved views yet. Save the current filters below.
            </div>
          )}

          {views.map((v) => (
            <div
              key={v.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-2)',
                borderRadius: 'var(--r-md)',
              }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => apply(v)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                  textAlign: 'left',
                  padding: '6px var(--sp-2)',
                  border: 'none',
                  borderRadius: 'var(--r-md)',
                  background: 'transparent',
                  color: 'var(--color-ink)',
                  fontFamily: 'inherit',
                  fontSize: 'var(--fs-sm)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{ fontWeight: 'var(--fw-medium)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                  {v.name}
                  {v.is_default && <span style={{ color: 'var(--color-ink-2)', fontWeight: 'var(--fw-regular, 400)' }}> · default</span>}
                </span>
                <span style={{ color: 'var(--color-ink-2)', fontSize: 'var(--fs-xs)' }}>{scopeLabel[v.scope]}</span>
              </button>
              {canDelete(v) && (
                <button
                  type="button"
                  aria-label={`Delete view ${v.name}`}
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(v.id)}
                  style={{
                    flexShrink: 0,
                    padding: '4px',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--color-ink-3)',
                    cursor: 'pointer',
                    borderRadius: 'var(--r-sm, 4px)',
                    lineHeight: 0,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--color-status-red)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--color-ink-3)'; }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              )}
            </div>
          ))}

          <div style={{ borderTop: '1px solid var(--color-border)', margin: 'var(--sp-2) 0', paddingTop: 'var(--sp-2)' }}>
            {!creating ? (
              <button
                type="button"
                onClick={() => setCreating(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  width: '100%',
                  padding: '6px var(--sp-2)',
                  border: 'none',
                  borderRadius: 'var(--r-md)',
                  background: 'transparent',
                  color: 'var(--color-primary, var(--color-ink))',
                  fontFamily: 'inherit',
                  fontSize: 'var(--fs-sm)',
                  fontWeight: 'var(--fw-medium)',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-bg)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Save current view
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)', padding: '0 var(--sp-1)' }}>
                <Input
                  autoFocus
                  placeholder="View name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitCreate(); }}
                />
                {canShare && (
                  <Checkbox
                    label="Share with team"
                    checked={shareWithTeam}
                    onChange={(e) => setShareWithTeam(e.target.checked)}
                  />
                )}
                <div style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}>
                  <Button variant="ghost" size="sm" onClick={() => { setCreating(false); setNewName(''); setShareWithTeam(false); }}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={submitCreate}
                    disabled={!newName.trim() || createMutation.isPending}
                  >
                    {createMutation.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
